// API Usage Tracker for CoinGecko API
// CoinGecko Free API Limits: 10-50 calls/minute (varies based on load)

class APIUsageTracker {
  constructor() {
    this.calls = [];
    this.storageKey = 'coingecko_api_calls';
    this.loadFromStorage();
  }

  // Load previous calls from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.calls = JSON.parse(stored);
        // Clean up old calls (older than 1 hour)
        this.cleanOldCalls();
      }
    } catch (e) {
      console.error('Error loading API usage:', e);
    }
  }

  // Save calls to localStorage
  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.calls));
    } catch (e) {
      console.error('Error saving API usage:', e);
    }
  }

  // Record a new API call
  recordCall(endpoint = 'coingecko') {
    const call = {
      timestamp: Date.now(),
      endpoint
    };
    this.calls.push(call);
    this.cleanOldCalls();
    this.saveToStorage();
  }

  // Remove calls older than 1 hour
  cleanOldCalls() {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    this.calls = this.calls.filter(call => call.timestamp > oneHourAgo);
  }

  // Get calls in last N minutes
  getCallsInLastMinutes(minutes = 1) {
    const timeAgo = Date.now() - (minutes * 60 * 1000);
    return this.calls.filter(call => call.timestamp > timeAgo);
  }

  // Get usage statistics
  getUsageStats() {
    this.cleanOldCalls();
    
    const lastMinute = this.getCallsInLastMinutes(1);
    const last5Minutes = this.getCallsInLastMinutes(5);
    const last15Minutes = this.getCallsInLastMinutes(15);
    const lastHour = this.calls;

    // CoinGecko free tier limits (approximate)
    const RATE_LIMIT_PER_MINUTE = 10; // Conservative estimate for free tier
    const RATE_LIMIT_PER_5MIN = 50;
    const RATE_LIMIT_PER_15MIN = 150;
    const RATE_LIMIT_PER_HOUR = 600;

    return {
      lastMinute: {
        calls: lastMinute.length,
        limit: RATE_LIMIT_PER_MINUTE,
        remaining: Math.max(0, RATE_LIMIT_PER_MINUTE - lastMinute.length),
        percentage: Math.min(100, (lastMinute.length / RATE_LIMIT_PER_MINUTE) * 100),
        status: lastMinute.length >= RATE_LIMIT_PER_MINUTE ? 'critical' : 
                lastMinute.length >= RATE_LIMIT_PER_MINUTE * 0.8 ? 'warning' : 'good'
      },
      last5Minutes: {
        calls: last5Minutes.length,
        limit: RATE_LIMIT_PER_5MIN,
        remaining: Math.max(0, RATE_LIMIT_PER_5MIN - last5Minutes.length),
        percentage: Math.min(100, (last5Minutes.length / RATE_LIMIT_PER_5MIN) * 100),
        status: last5Minutes.length >= RATE_LIMIT_PER_5MIN ? 'critical' : 
                last5Minutes.length >= RATE_LIMIT_PER_5MIN * 0.8 ? 'warning' : 'good'
      },
      last15Minutes: {
        calls: last15Minutes.length,
        limit: RATE_LIMIT_PER_15MIN,
        remaining: Math.max(0, RATE_LIMIT_PER_15MIN - last15Minutes.length),
        percentage: Math.min(100, (last15Minutes.length / RATE_LIMIT_PER_15MIN) * 100),
        status: last15Minutes.length >= RATE_LIMIT_PER_15MIN ? 'critical' : 
                last15Minutes.length >= RATE_LIMIT_PER_15MIN * 0.8 ? 'warning' : 'good'
      },
      lastHour: {
        calls: lastHour.length,
        limit: RATE_LIMIT_PER_HOUR,
        remaining: Math.max(0, RATE_LIMIT_PER_HOUR - lastHour.length),
        percentage: Math.min(100, (lastHour.length / RATE_LIMIT_PER_HOUR) * 100),
        status: lastHour.length >= RATE_LIMIT_PER_HOUR ? 'critical' : 
                lastHour.length >= RATE_LIMIT_PER_HOUR * 0.8 ? 'warning' : 'good'
      },
      totalCalls: this.calls.length,
      recentCalls: this.calls.slice(-10).reverse()
    };
  }

  // Check if we should throttle requests
  shouldThrottle() {
    const stats = this.getUsageStats();
    return stats.lastMinute.status === 'critical' || stats.last5Minutes.status === 'critical';
  }

  // Get next safe call time
  getNextSafeCallTime() {
    const stats = this.getUsageStats();
    if (stats.lastMinute.calls >= stats.lastMinute.limit) {
      const oldestCall = this.getCallsInLastMinutes(1)[0];
      if (oldestCall) {
        return oldestCall.timestamp + (60 * 1000); // Wait until oldest call expires
      }
    }
    return Date.now();
  }

  // Reset tracking
  reset() {
    this.calls = [];
    this.saveToStorage();
  }

  // Get formatted report
  getFormattedReport() {
    const stats = this.getUsageStats();
    
    return `
╔════════════════════════════════════════════╗
║     CoinGecko API Usage Report             ║
╠════════════════════════════════════════════╣
║ Last 1 Minute:                             ║
║   Calls: ${stats.lastMinute.calls}/${stats.lastMinute.limit}                              ║
║   Remaining: ${stats.lastMinute.remaining}                               ║
║   Status: ${stats.lastMinute.status.toUpperCase()}                           ║
║                                            ║
║ Last 5 Minutes:                            ║
║   Calls: ${stats.last5Minutes.calls}/${stats.last5Minutes.limit}                             ║
║   Remaining: ${stats.last5Minutes.remaining}                              ║
║   Status: ${stats.last5Minutes.status.toUpperCase()}                           ║
║                                            ║
║ Last 15 Minutes:                           ║
║   Calls: ${stats.last15Minutes.calls}/${stats.last15Minutes.limit}                            ║
║   Remaining: ${stats.last15Minutes.remaining}                             ║
║   Status: ${stats.last15Minutes.status.toUpperCase()}                           ║
║                                            ║
║ Last Hour:                                 ║
║   Calls: ${stats.lastHour.calls}/${stats.lastHour.limit}                            ║
║   Remaining: ${stats.lastHour.remaining}                            ║
║   Status: ${stats.lastHour.status.toUpperCase()}                           ║
╚════════════════════════════════════════════╝

Note: CoinGecko Free API has rate limits.
Consider caching responses to reduce API calls.
    `;
  }
}

// Create singleton instance
const apiUsageTracker = new APIUsageTracker();

export default apiUsageTracker;

// Export helper function to wrap fetch calls
export const trackApiCall = async (fetchFunction) => {
  // Check if we should throttle
  if (apiUsageTracker.shouldThrottle()) {
    const nextSafeTime = apiUsageTracker.getNextSafeCallTime();
    const waitTime = Math.max(0, nextSafeTime - Date.now());
    
    if (waitTime > 0) {
      console.warn(`⏳ Rate limit approaching. Waiting ${waitTime}ms before next call...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  // Record the call
  apiUsageTracker.recordCall();
  
  // Execute the fetch
  return await fetchFunction();
};
